import { applySnapshot, destroy, detach, getSnapshot, getType, Instance, types } from 'mobx-state-tree';
import { getNPhotosBucket } from 'src/_shared/utils/get-n-photos-bucket/getNPhotosBucket';
import {
  ImageFastThumbnailAdded,
  ImageFastThumbnailsDeleted,
  ProjectAdded,
  ProjectSaved,
  ProjectSaving,
  ProjectUpdated,
  ImageFastThumbnailUpdated,
  ThumbnailProgressUpdated,
  ImageShareStart,
  ImageShareUploadCount,
  ImageShareComplete,
  ImageShareNewImageNotification,
  ShareImageNewNotificationItem,
  ImageShareDownloadError,
  SDK_SHARE_DOWNLOAD_ERRORS,
} from '@photosi/expo-photosi-sdk/build/Project';
import { ProjectModel } from '../_models/project';
import { BaseListenersModel } from '../_models/_base/baseListenersModel';
import { parseProduct, parseProductV3, parseProject, userImageParser, userTextsParser } from '../_models/parsers';
import {
  ProductAdded,
  ProductDeleted,
  ProductsAdded,
  ProductsDeleted,
  ProductsReplaced,
  ProductUpdated,
} from '@photosi/expo-photosi-sdk/build/Product';
import { IProductModel, ProductModel } from '../_models/product';
import { ThumbnailImageModel, ThumbnailImageModelInstance } from '../_models/thumbnailImage';
import { UserImageAdded, UserImageDeleted, UserImageUpdated } from '@photosi/expo-photosi-sdk/build/UserImage';
import { UserImageModel } from '../_models/userImage';
import { SAVING_STATE } from '../page-project/pageProject.constants';
import { IProjectPageModel } from '../page-project/pageProject';
import { DynamicModalInstance } from '../dynamic-modal/dynamicModal';
import { PhotoDrawerInstance } from '../photo-drawer/photoDrawer';
import { ProductReplacementData } from '@photosi/expo-photosi-sdk/build/Product';
import { UserTextModel } from '../_models/userText';
import { ImageData } from '@photosi/expo-photosi-sdk/build/Project';
import i18n from 'src/app/i18n';
import { DYNAMIC_MODAL_GROUPS } from '../toast/toastStore.types';
import { TOAST_IDS } from '@photosi/rn-components/toast/Toast.constants';
import { ImageShareFailure, ProjectProductsNotUploaded } from '@photosi/expo-photosi-sdk/build/PhotosiSdk';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { DYNAMIC_MODAL_VIEWS, SELECTED_MENU_ITEMS } from '../_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { SourcesEventType } from '@photosi/rn-components/modals/dynamic-modals/modal-upload-multisource/ModalUploadMultisource.constants';
import { DRAWER_VIEWS } from '../drawer/drawer.constants';
import Datadog from '../../_shared/datadog';
import { DATADOG_CUSTOM_ACTIONS } from '../../_shared/datadog/DatadogCustomActions';
import { BOTTOM_SHEET_VIEWS } from '../bottom-sheet/bottomSheet.constants';
import { isEditorNew } from 'src/helpers/url.helper';
import { icon } from 'src/theme/colors';
import { ANIMATED_ICON } from '@photosi/rn-components/animated-icon/AnimatedIcon.constants';
import UploadErrorCode from 'src/helpers/upload.helper';
import { UserImageReplaced } from '@photosi/expo-photosi-sdk/build/UserImage';

export type TData = typeof Data;
export type DataInstance = Instance<TData>;
export interface IData extends DataInstance {}

export const Data = BaseListenersModel.named('Data')
  .props({
    isLoading: types.boolean,
    projects: types.optional(types.array(ProjectModel), []),
  })
  .volatile<{
    thumbnailTemp: ImageData[];
    debounceTimeout: NodeJS.Timeout | undefined;
    debounceCounter: NodeJS.Timeout | undefined;
    percentage: number;
    uploadError: boolean;
    errorCode: string;
    fileListError: string[];
    fileErroCodes: string[];
    isImageShareStarted: boolean;
    showNewImageNotification: boolean;
    notificationFiles: ShareImageNewNotificationItem[];
    newEditChanges: boolean;
    currentEditorVersion: 'v1' | 'v2' | 'v3' | undefined;
    photoPickerToFirstImagePending: boolean;
    photoPickerToFirstImageNPhotos: number;
    addToCartReadyPending: boolean;
    addToCartReadyNPhotos: number;
    isReplacingPhoto: string | undefined;
  }>(self => ({
    thumbnailTemp: [],
    debounceTimeout: undefined,
    debounceCounter: undefined,
    percentage: 0,
    uploadError: false,
    errorCode: '',
    fileListError: [],
    fileErroCodes: [],
    isImageShareStarted: false,
    showNewImageNotification: false,
    notificationFiles: [],
    newEditChanges: false,
    currentEditorVersion: undefined,
    photoPickerToFirstImagePending: false,
    photoPickerToFirstImageNPhotos: 0,
    addToCartReadyPending: false,
    addToCartReadyNPhotos: 0,
    isReplacingPhoto: undefined,
  }))
  .views(self => ({
    get projectStore(): IProjectPageModel {
      return self.root.project;
    },
    get dynamicModal(): DynamicModalInstance {
      return self.root.dynamicModal;
    },
    get photoDrawer(): PhotoDrawerInstance {
      return self.root.photoDrawer;
    },
  }))
  .actions(self => ({
    onProjectAddedOrUpdated(event: ProjectAdded | ProjectUpdated) {
      // console.log('DataModel(onProjectAddedOrUpdated) - event: ', event);

      let project = self.projects.find(project => project.id === event.id);
      if (!project) {
        project = ProjectModel.create(parseProject(event));
        self.projects.push(project);
      } else {
        const parsedProject = parseProject(event);
        const newProject = {
          ...parsedProject,
          products: project.products,
          thumbnailImages: project.thumbnailImages,
          // groups definitions will never be updated
          groups: project.groups,
          productsOptions: project.productsOptions,
        };

        applySnapshot(project, newProject);
      }

      // seleziono il primo progetto
      if (!self.projectStore.activeProject) {
        self.projectStore.setActiveProject(project);
      }
      // console.log('DataModel(onProjectAddedOrUpdated) - getSnapshot: ', getSnapshot(self));
    },
    onProjectDeleted(event: any) {
      // console.log('DataModel(onProjectDeleted) - event: ', event);

      const project = self.projects.find(project => project.id === event.id);
      if (project) {
        // detach and destroy before splice
        detach(project);
        destroy(project);
      }

      // console.log('DataModel(onProjectDeleted) - getSnapshot: ', getSnapshot(self));
    },
    onProjectSaving(event: ProjectSaving) {
      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onProjectSaved) - no project found: ', event);
        return;
      }
      project.setSavingState(SAVING_STATE.saving);
    },
    onProjectSaved(event: ProjectSaved) {
      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onProjectSaved) - no project found: ', event);
        return;
      }
      project.setLastSaveTimestamp(event.timeStamp);
      project.setSavingState(SAVING_STATE.saved);
      self.newEditChanges = true;
    },
    onProjectProductsNotUploaded(event: ProjectProductsNotUploaded) {
      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onProjectSaved) - no project found: ', event);
        return;
      }
      self.root.toastStore.showProjectProductsNotUploadedToast({
        id: TOAST_IDS.ProjectProductsNotUploaded,
        message: i18n.t('toast:project-not-uploaded'),
        removeOnPress: true,
        ctaText: i18n.t('toast:accept'),
        onPress: () => {
          self.root.toastStore.removeGroupToast(DYNAMIC_MODAL_GROUPS.ProjectProductsNotUploaded);
        },
      });
    },
    onProductAddedOrUpdated(
      event: ProductAdded | ProductUpdated,
      triggerSwitchToGroup = false,
    ): IProductModel | undefined {
      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onProductAddedOrUpdated) - no project found: ', event);
        return;
      }
      const parsedProduct = isEditorNew ? parseProductV3(event) : parseProduct(event);
      // create group instance if not present
      const group = project.groups.find(g => g.id === parsedProduct.groupId);
      const isNewGroup = !group;
      const placeholderGroup = project.groupTypes.find(g => g.code === parsedProduct.groupCode);
      if (isNewGroup && placeholderGroup) {
        const newGroup = getType(placeholderGroup).create({
          ...getSnapshot(placeholderGroup),
          id: parsedProduct.groupId,
          groupInfo: parsedProduct.groupingInfo,
          groupIdSDK: parsedProduct.groupIdSDK,
          isPlaceholder: false,
        });
        project.groups.push(newGroup);
      } else if (!isNewGroup) {
        group.syncProductsOptions();
      }

      let product = project.products.find(product => product.id === parsedProduct.id);
      if (!product) {
        product = ProductModel.create(parsedProduct);

        // const isFirstProductAdded = project && project.products.length === 0;
        // if (triggerSwitchToGroup) {
        //   self.projectStore.switchToGroup({
        //     groupId: product.groupId,
        //     scrollTo: product,
        //     forceOpening: triggerSwitchToGroup,
        //   });
        // }

        project.products.push(product);

        project.setAlreadyAddedProductCount(project.alreadyAddedProductCount + 1);

        // add product to group
        const group = project.groups.find(group => group.id === product?.groupId);
        if (group) group.products.push(product);

        // TODO: da disabilitare nel nuovo flusso editor-new
        // se non c'è già un prodotto attivo allora seleziono il primo prodotto
        const projectStore = self.projectStore;
        if (!projectStore.activeProduct) {
          projectStore.setActiveProduct(product);
        }
      } else {
        // product.onUpdate(event);

        const newProduct = {
          ...parsedProduct,
          userImages: product.userImages,
          userTexts: product.userTexts,
        };

        const isGroupChanged = product.groupId !== newProduct.groupId;
        if (isGroupChanged) {
          // move product to new group
          const oldGroup = project.groups.find(group => group.id === product?.groupId);
          if (oldGroup) oldGroup.removeProductFromGroup(product);
          const newGroup = project.groups.find(group => group.id === newProduct.groupId);
          if (newGroup) newGroup.addProductToGroup(product);
        }

        applySnapshot(product, newProduct);

        // updates references between UserImageAreas and UserImages
        product.syncImageAreasAndImages();
        // updates products options
        project.syncProductsOptions();

        // handle new quality suggestions on change product lookup
        product.userImages.forEach(userImage => {
          if (userImage.qualityAlert) userImage.loadQualitySuggestions();
          userImage.refreshEditor();
        });

        // remove opacity
        product.setIsUpdating(false);
      }

      if (self.isLoading) {
        // when the first product is added or updated then remove the loader
        self.setLoading(false);
      }

      // console.log('DataModel(onProductAddedOrUpdated) - getSnapshot: ', getSnapshot(self));
      return product;
    },
    onProductDeleted(event: ProductDeleted) {
      // console.log('DataModel(ProductDeleted) - event: ', event);

      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(ProductDeleted) - no project found: ', event);
        return;
      }

      const product = project.products.find(product => product.id === event.id);
      if (!product) {
        // console.log('DataModel(ProductDeleted) - no product found: ', event);
        return;
      }

      // Removes referenced product in group before detroy
      const group = project.groups.find(group => group.id === product.groupId);
      if (group) {
        group.removeProductFromGroup(product);
      } else {
        // detach and destroy
        detach(product);
        destroy(product);
      }

      // console.log('DataModel(ProductDeleted) - getSnapshot: ', getSnapshot(self));
    },

    onImageFastThumbnailAddedOrUpdated(event: ImageData) {
      console.log('DataModel(onImageFastThumbnailAddedOrUpdated) - event: ', event);
      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onImageFastThumbnailAddedOrUpdated) - no project found: ', event);
        return;
      }
      let thumbnail = project.thumbnailImages.find(thumbnail => thumbnail.originalImageId === event.originalImageId);
      if (!thumbnail) {
        thumbnail = ThumbnailImageModel.create(event as ThumbnailImageModelInstance);
        // IF FIRST UPLOAD
        thumbnail.usageQuantity === 0 && thumbnail.select();
        project.thumbnailImages.push(thumbnail);
      } else {
        applySnapshot(thumbnail, event as ThumbnailImageModelInstance);
      }
      // console.log('DataModel(onImageFastThumbnailAddedOrUpdated) - getSnapshot: ', getSnapshot(self));
    },

    onUserImageAddedOrUpdated(event: UserImageAdded | UserImageUpdated) {
      // console.log('DataModel(onUserImageAddedOrUpdated) - event: ', event);
      if (self.debounceCounter) clearTimeout(self.debounceCounter);

      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onUserImageAddedOrUpdated) - no project found: ', event);
        return;
      }

      // updates thumbnails counter
      self.debounceCounter = setTimeout(() => {
        project.loadThumbnailImages();
      }, 500);

      const product = project.products.find(product => product.id === event.productId);
      if (!product) {
        // console.log('DataModel(onUserImageAddedOrUpdated) - no product found: ', event);
        return;
      }

      let userImage = product.userImages.find(userImage => userImage.id === event.id);
      if (!userImage) {
        userImage = UserImageModel.create(userImageParser(event));
        product.userImages.push(userImage);
      } else {
        const newUserImage = {
          ...userImageParser(event),
          // thumbnailImage: userImage.thumbnailImage
        };
        applySnapshot(userImage, newUserImage);

        product.setIsUpdating(false);
        userImage.setIsUpdating(false);
      }

      // updates references between UserImageAreas and UserImages
      product.syncImageAreasAndImages();

      //sync for old editor frame
      product?.lookups?.forEach(item => {
        item.syncBorderTypeSelection(event);
        item.getQualitySuitableFormats();
      });

      // closes uploading modal
      if (self.dynamicModal.activeModal?.isUploadingPhoto) {
        self.dynamicModal.activeModal?.setIsUploadingPhoto(false);
        self.projectStore.activeProject?.deselectAllThumbnailImages();
        self.photoDrawer.setImagesUploaded(true);
        self.dynamicModal.closeModal();
      }

      // console.log('DataModel(onUserImageAddedOrUpdated) - getSnapshot: ', getSnapshot(self));
    },
    onUserImageDeleted(event: UserImageDeleted) {
      // console.log('DataModel(onUserImageDeleted) - event: ', event);

      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onUserImageDeleted) - no project found: ', event);
        return;
      }

      // updates thumbnails counter
      self.debounceCounter = setTimeout(() => {
        project.loadThumbnailImages();
      }, 500);

      const product = project.products.find(product => product.id === event.productId);
      if (!product) {
        // console.log('DataModel(onUserImageAddedOrUpdated) - no product found: ', event);
        return;
      }

      let userImage = product.userImages.find(userImage => userImage.id === event.id);
      if (userImage) {
        // detach and destroy
        detach(userImage);
        destroy(userImage);
      }

      // console.log('DataModel(onUserImageDeleted) - getSnapshot: ', getSnapshot(self));
    },
    onUserTextAddedOrUpdated(event: UserImageAdded | UserImageUpdated) {
      // console.log('DataModel(onUserTextAddedOrUpdated) - event: ', event);

      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onUserTextAddedOrUpdated) - no project found: ', event);
        return;
      }

      const product = project.products.find(product => product.id === event.productId);
      if (!product) {
        // console.log('DataModel(onUserTextAddedOrUpdated) - no product found: ', event);
        return;
      }

      const eventUserTexts = event.transformations.userTexts;
      if (!eventUserTexts) {
        // console.log('DataModel(onUserTextAddedOrUpdated) - no userTexts found: ', event);
        return;
      }

      eventUserTexts.forEach(eventUserText => {
        let userText = product.userTexts.find(ut => ut.id === eventUserText.id);
        if (!userText) {
          userText = UserTextModel.create(userTextsParser(eventUserText, project.id, product.id));
          product.userTexts.push(userText);
        } else {
          const newUserText = {
            ...userTextsParser(eventUserText, project.id, product.id),
          };
          applySnapshot(userText, newUserText);
        }
      });

      // console.log('DataModel(onUserTextAddedOrUpdated) - getSnapshot: ', getSnapshot(self));
    },
    onImageFastThumbnailDeleted(event: ImageFastThumbnailsDeleted) {
      // console.log('DataModel(onImageFastThumbnailDeleted) - event: ', event);

      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onImageFastThumbnailAddedOrUpdated) - no project found: ', event);
        return;
      }

      event.deletedOriginalIds?.forEach(id => {
        let thumbnailToDelete = project.thumbnailImages?.find(thumbnail => thumbnail.originalImageId === id);
        if (thumbnailToDelete) {
          detach(thumbnailToDelete);
          destroy(thumbnailToDelete);
        }
      });

      // console.log('DataModel(onImageFastThumbnailDeleted) - getSnapshot: ', getSnapshot(self));
    },
    setPercentage(percentage: number) {
      self.percentage = percentage;
    },
  }))
  .actions(self => ({
    onProductsAdded(event: ProductsAdded) {
      // console.log('DataModel(onProductsAdded) - event: ', event);

      const uniqueGroupIds = new Set(event.items.map(product => product.groupId));
      const isOnlyOneGroup = uniqueGroupIds.size === 1;

      event.items.forEach((eventProduct, index) => {
        const productModel = self.onProductAddedOrUpdated(eventProduct);
        if (productModel && event?.isFirstBatch) {
          // switcha to product group, force open it only if it's the only one
          self.projectStore.switchToGroup({
            groupId: productModel.groupId,
            scrollTo: productModel,
            forceOpening: event.isFirstBatch && isOnlyOneGroup,
          });
        }
      });
      if (event?.isLastBatch) {
        self.setPercentage(0);
        self.root.project.setUploading(false);
        self.root.toastStore.showInfoToast({
          message: i18n.t('toast:products-added', { count: event?.totalProducts }),
          iconColor: icon.iconSuccess,
          id: 'toast-products-added',
          removeOnPress: false,
          onPress: () => {},
          animatedIcon: ANIMATED_ICON.CheckMark,
          filled: true,
          duration: 3000,
        });
      }

      // console.log('DataModel(onProductsAdded) - getSnapshot: ', getSnapshot(self));
    },
    onProductsReplaced(event: ProductsReplaced) {
      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onProductsReplaced) - no project found: ', event);
        return;
      }

      // console.log('DataModel(onProductsReplaced) - event: ', event);
      event.products.forEach((item: ProductReplacementData, index: number) => {
        if (item.productUpdated) {
          const productModel = self.onProductAddedOrUpdated(item.productUpdated);
          const isFirstProduct = index === 0;
          if ((item.productChangedGroup || isEditorNew) && isFirstProduct && productModel) {
            // Scroll to first new product
            self.projectStore.switchToGroup({
              groupId: productModel.groupId,
              scrollTo: productModel,
            });
          }
        }
        if (item.userImageUpdated) {
          item.userImageUpdated.forEach((userImageUpdated: UserImageUpdated) => {
            self.onUserImageAddedOrUpdated(userImageUpdated);
            self.onUserTextAddedOrUpdated(userImageUpdated);
          });
        }
        if (item.userImageDeleted) {
          item.userImageDeleted.forEach((userImageDeleted: UserImageDeleted) =>
            self.onUserImageDeleted(userImageDeleted),
          );
        }
      });

      // console.log('DataModel(onProductsReplaced) - getSnapshot: ', getSnapshot(self));
    },

    onProductsDeleted(event: ProductsDeleted) {
      // console.log('DataModel(ProductsDeleted) - event: ', event);

      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(ProductsDeleted) - no project found: ', event);
        return;
      }

      event.products.forEach(productDeletionData => {
        productDeletionData.userImagesDeleted.forEach((userImageDeleted: UserImageDeleted) => {
          self.onUserImageDeleted(userImageDeleted);
        });

        const productDeleted: ProductDeleted = {
          projectId: event.projectId,
          id: productDeletionData.id,
        };
        self.onProductDeleted(productDeleted);
      });

      // console.log('DataModel(ProductsDeleted) - getSnapshot: ', getSnapshot(self));
    },
    onImageShareFailure(event: ImageShareFailure) {
      // console.log('DataModel(onImageShareFailure) - event: ', event);

      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onImageShareFailure) - no project found: ', event);
        return;
      }
      self.root.project.stopShareObserver();
      self.root.project.startShareObserver();
    },
    onImageShareStart(event: ImageShareStart) {
      // console.log('DataModel(onImageShareStart) - event: ', event);

      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onImageShareStart) - no project found: ', event);
        return;
      }

      if (self.root.drawer.drawerVisible) {
        if (
          self.root.drawer.drawerView === DRAWER_VIEWS.PhotoDrawer &&
          self.root.photoDrawer.picker !== SELECTED_MENU_ITEMS.Computer
        )
          self.root.photoDrawer.setPicker(SELECTED_MENU_ITEMS.Computer);
      }
    },
    onImageShareUploadCount(event: ImageShareUploadCount) {
      // console.log('DataModel(onImageShareUploadCount) - event: ', event);

      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onImageShareUploadCount) - no project found: ', event);
        return;
      }

      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourcePhotoUploaded, {
        n_photo: event.uploadCount,
        source_type: event.source === 'smartphone' ? SourcesEventType.Smartphone : SourcesEventType.FamilyFriends,
      });
    },
    onImageShareComplete(event: ImageShareComplete) {
      // console.log('DataModel(onImageShareComplete) - event: ', event);

      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onImageShareComplete) - no project found: ', event);
        return;
      }
    },
    onImageShareDownloadError(event: ImageShareDownloadError) {
      // console.log('DataModel(onImageShareDownloadError) - event: ', event);

      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onImageShareDownloadError) - no project found: ', event);
        return;
      }
      let uploadErrorData;
      let onPressDiscardChanges;

      switch (event.errorCode) {
        case SDK_SHARE_DOWNLOAD_ERRORS.INVALID_DOWNLOAD:
          uploadErrorData = {
            titleKey: 'upload:error:corrupted-files:title',
            descriptionKey: 'upload:error:corrupted-files:description',
            errorItems: event.errorItems ?? [],
            ctaKey: 'upload:error:corrupted-files:cta',
          };
          onPressDiscardChanges = () => {};
          break;
        case SDK_SHARE_DOWNLOAD_ERRORS.REMOTE_FETCH_ERROR:
        case SDK_SHARE_DOWNLOAD_ERRORS.CONNECTION_ERROR:
        case SDK_SHARE_DOWNLOAD_ERRORS.DOWNLOAD_ERROR:
          uploadErrorData = {
            titleKey: 'upload:error:network-unavailable:title',
            descriptionKey: 'upload:error:network-unavailable:description',
            errorItems: [],
            ctaKey: 'upload:error:network-unavailable:cta',
          };
          onPressDiscardChanges = () => {
            self.root.project.stopShareObserver();
            self.root.project.startShareObserver();
          };
          break;
        default:
          uploadErrorData = {
            titleKey: 'upload:error:unknown:title',
            descriptionKey: 'upload:error:unknown:description',
            errorItems: [],
            ctaKey: 'upload:error:unknown:cta',
          };
          onPressDiscardChanges = () => {};
          break;
      }

      self.root.dynamicModal.openModal({
        dynamicModalView: DYNAMIC_MODAL_VIEWS.ModalUploadError,
        uploadErrorData,
        onPressDiscardChanges,
      });
    },
    onImageShareNewImageNotification(event: ImageShareNewImageNotification) {
      const project = self.projects.find(project => project.id === event.projectId);
      if (!project) {
        // console.log('DataModel(onImageShareComplete) - no project found: ', event);
        return;
      }
      self.notificationFiles = event.files;
      self.showNewImageNotification = true;
      self.root.project.setShowNotificationPhotoDrawer(true);
      self.root.bottomSheet.openBottomSheet({
        bottomSheetView: BOTTOM_SHEET_VIEWS.SharePhotos,
        showDragIndicator: false,
        showOpacityBackground: true,
      });
    },
    onUserImageReplaced(event: UserImageReplaced) {
      if (event.productId) {
        setTimeout(() => {
          self.root.toastStore.showInfoToast({
            duration: 3000,
            message: i18n.t('toast:product-replaced'),
            id: 'toast-product-replaced',
          });
        }, 1000);
      }
    },
  }))
  .actions(self => ({
    addThumbnailTemp(imageData: ImageData) {
      self.thumbnailTemp.push(imageData);
    },
    flushThumbnailTemp() {
      if (self.root.dynamicModal?.activeModal?.isUploadingPhoto)
        self.root.dynamicModal.activeModal.setIsUploadingPhoto(false);
      self.thumbnailTemp.forEach(imageDataTemp => {
        self.onImageFastThumbnailAddedOrUpdated(imageDataTemp);
      });
      self.thumbnailTemp = [];
    },
    setDebouncedCallback(debouncedCallback: any, debounceTime = 500) {
      self.debounceTimeout = setTimeout(debouncedCallback, debounceTime);
    },
    clearDebouncedCallback() {
      if (self.debounceTimeout) clearTimeout(self.debounceTimeout);
    },
    setLoading(loading: boolean) {
      self.isLoading = loading;
    },
    setIsImageShareStarted(isImageShareStarted: boolean) {
      self.isImageShareStarted = isImageShareStarted;
    },
    setUploadError(error: boolean) {
      self.uploadError = error;
      if (!error) {
        self.errorCode = '';
        self.fileListError = [];
        self.fileErroCodes = [];
      }
    },
    setFileListError(files: string[]) {
      self.fileListError = files;
    },
    setShowNewImageNotification(value: boolean) {
      self.showNewImageNotification = value;
    },
    setReplacingPhoto(value: string | undefined) {
      self.isReplacingPhoto = value;
    },
    handleUploadError(file: string, errorCode?: string) {
      // The `percentage === 100` this used to carry was the *batch* progress: it meant
      // "stop the spinner only if the whole batch already finished", which a batch dying
      // at 40% never does — so the spinner stayed up forever, and LoadingStep suppresses
      // the error banner while it is up. Every other reset site lowers the flag
      // unconditionally at the first sign of progress (the first ImageFastThumbnailAdded),
      // so lowering it at the first error is the symmetric rule, not a new one.
      if (self.root.dynamicModal?.activeModal?.isUploadingPhoto)
        self.root.dynamicModal.activeModal.setIsUploadingPhoto(false);
      if (!self.uploadError) self.uploadError = true;
      self.fileListError.push(file);
      if (!!errorCode) {
        const errorCodeAnalytic = UploadErrorCode.get(errorCode) ?? '';
        if (self.fileErroCodes.indexOf(errorCodeAnalytic) < 0) {
          self.fileErroCodes.push(errorCodeAnalytic);
        }
        self.errorCode = errorCode;
      }
    },
    sendErrorAnalytic() {
      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.loadPhotosFromSourceError, {
        error: self.fileErroCodes,
      });
    },
    setNewEditChanges(value: boolean) {
      self.newEditChanges = value;
    },
    setEditorVersion(version: 'v1' | 'v2' | 'v3' | undefined) {
      self.currentEditorVersion = version;
    },
    startPhotoPickerToFirstImageMeasure(nPhotos: number) {
      // Snapshot one-shot al confirm del picker: emette anche se l'utente non
      // arriva mai in griglia (errore, abandon, crash). Indipendente dalla
      // measure di durata sotto.
      if (nPhotos > 0) {
        Datadog.logInfo(DATADOG_CUSTOM_ACTIONS['photo_picker_selection'], {
          n_photos: nPhotos,
          n_photos_bucket: getNPhotosBucket(nPhotos),
          editor_version: self.currentEditorVersion ?? 'unknown',
        });
      }

      self.photoPickerToFirstImagePending = true;
      self.photoPickerToFirstImageNPhotos = nPhotos;
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS['photo_picker_to_first_image_time']);
    },
    endPhotoPickerToFirstImageMeasure() {
      if (!self.photoPickerToFirstImagePending) return;
      const n = self.photoPickerToFirstImageNPhotos;
      Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS['photo_picker_to_first_image_time'], {
        n_photos: n,
        n_photos_bucket: getNPhotosBucket(n),
        editor_version: self.currentEditorVersion ?? 'unknown',
      });
      self.photoPickerToFirstImagePending = false;
      self.photoPickerToFirstImageNPhotos = 0;

      // Continuation measure: dal primo render in griglia al momento in cui
      // il pulsante "Aggiungi al carrello" diventa cliccabile (transizione
      // isAddToCartDisabled true → false in pageProject.applyEnableAddToCart).
      self.addToCartReadyPending = true;
      self.addToCartReadyNPhotos = n;
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS['add_to_cart_ready_time']);
    },
    endAddToCartReadyMeasure() {
      if (!self.addToCartReadyPending) return;
      const n = self.addToCartReadyNPhotos;
      Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS['add_to_cart_ready_time'], {
        n_photos: n,
        n_photos_bucket: getNPhotosBucket(n),
        editor_version: self.currentEditorVersion ?? 'unknown',
      });
      self.addToCartReadyPending = false;
      self.addToCartReadyNPhotos = 0;
    },
    cleanAddToCartReadyMeasure() {
      if (!self.addToCartReadyPending) return;
      Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS['add_to_cart_ready_time']);
      self.addToCartReadyPending = false;
      self.addToCartReadyNPhotos = 0;
    },
    cleanPhotoPickerToFirstImageMeasure() {
      if (!self.photoPickerToFirstImagePending) return;
      Datadog.cleanMeasure(DATADOG_CUSTOM_ACTIONS['photo_picker_to_first_image_time']);
      self.photoPickerToFirstImagePending = false;
      self.photoPickerToFirstImageNPhotos = 0;
    },
  }))
  .actions(self => ({
    afterAttach() {
      // ON PROJECT ADDED
      self.addListener('ProjectAdded', (event: ProjectAdded) => {
        console.log('#event - DataModel(ProjectAdded) - event: ', event);
        self.onProjectAddedOrUpdated(event);
        // console.log('#event - DataModel(ProjectAdded) - getSnapshot: ', getSnapshot(self));
      });
      // ON PROJECT UPDATED
      self.addListener('ProjectUpdated', (event: ProjectAdded) => {
        console.log('#event - DataModel(ProjectUpdated) - event: ', event);
        self.onProjectAddedOrUpdated(event);
        // console.log('#event - DataModel(ProjectUpdated) - getSnapshot: ', getSnapshot(self));
      });
      // ON PROJECT DELETED
      self.addListener('ProjectDeleted', (event: any) => {
        console.log('#event - DataModel(ProjectDeleted) - event: ', event);
        self.onProjectDeleted(event);
        // console.log('#event - DataModel(ProjectDeleted) - getSnapshot: ', getSnapshot(self));
      });
      // ON PROJECT SAVING
      self.addListener('ProjectSaving', (event: ProjectSaving) => {
        console.log('#event - DataModel(ProjectSaving) - event: ', event);
        self.onProjectSaving(event);
        // console.log('#event - DataModel(ProjectSaving) - getSnapshot: ', getSnapshot(self));
      });
      // ON PROJECT SAVED
      self.addListener('ProjectSaved', (event: ProjectSaved) => {
        console.log('#event - DataModel(ProjectSaved) - event: ', event);
        self.onProjectSaved(event);
        // console.log('#event - DataModel(ProjectSaved) - getSnapshot: ', getSnapshot(self));
      });
      // ON PROJECT PRODUCT NOT UPLOADED
      self.addListener('ProjectProductsNotUploaded', (event: ProjectProductsNotUploaded) => {
        console.log('#event - DataModel(ProjectProductsNotUploaded) - event: ', event);
        self.onProjectProductsNotUploaded(event);
        // console.log('#event - DataModel(ProjectProductsNotUploaded) - getSnapshot: ', getSnapshot(self));
      });

      // ON PRODUCTS ADDED
      self.addListener('ProductsAdded', (event: ProductsAdded) => {
        console.log('#event - DataModel(ProductsAdded) - event: ', event);
        self.onProductsAdded(event);
        // console.log('#event - DataModel(ProductsAdded) - getSnapshot: ', getSnapshot(self));
      });
      // ON PRODUCT ADDED
      self.addListener('ProductAdded', (event: ProductAdded) => {
        console.log('#event - DataModel(ProductAdded) - event: ', event);
        const project = self.projects.find(project => project.id === event.projectId);
        if (!project) return;
        const isFirstProductAdded = project && project.products.length === 0;

        const productModel = self.onProductAddedOrUpdated(event);

        if (productModel && isFirstProductAdded) {
          // switcha to product group and force open it
          self.projectStore.switchToGroup({
            groupId: productModel.groupId,
            scrollTo: productModel,
            forceOpening: true,
          });
        }
        // console.log('#event - DataModel(ProductAdded) - getSnapshot: ', getSnapshot(self));
      });
      // ON PRODUCT UPDATED
      self.addListener('ProductUpdated', (event: ProductUpdated) => {
        console.log('#event - DataModel(ProductUpdated) - event: ', event);
        self.onProductAddedOrUpdated(event);
        // console.log('#event - DataModel(ProductUpdated) - getSnapshot: ', getSnapshot(self));
      });
      // ON PRODUCTS REPLACED
      self.addListener('ProductsReplaced', (event: ProductsReplaced) => {
        console.log('#event - DataModel(ProductsReplaced) - event: ', event);
        self.onProductsReplaced(event);
        // console.log('#event - DataModel(ProductsReplaced) - getSnapshot: ', getSnapshot(self));
      });
      // ON PRODUCT DELETED
      self.addListener('ProductDeleted', (event: ProductDeleted) => {
        console.log('#event - DataModel(ProductDeleted) - event: ', event);
        self.onProductDeleted(event);
        // console.log('#event - DataModel(ProductDeleted) - getSnapshot: ', getSnapshot(self));
      });
      // ON PRODUCTS DELETED
      self.addListener('ProductsDeleted', (event: ProductsDeleted) => {
        console.log('#event - DataModel(ProductsDeleted) - event: ', event);
        self.onProductsDeleted(event);
        // console.log('#event - DataModel(ProductsDeleted) - getSnapshot: ', getSnapshot(self));
      });
      // ON THUMBNAIL ADDED
      self.addListener('ImageFastThumbnailAdded', (event: ImageFastThumbnailAdded) => {
        console.log('#event - DataModel(ImageFastThumbnailAdded) - event: ', event);
        if (self.root.dynamicModal?.activeModal?.isUploadingPhoto)
          self.root.dynamicModal.activeModal.setIsUploadingPhoto(false);
        // self.clearDebouncedCallback();
        // self.addThumbnailTemp(event?.imageData);
        // if (self.thumbnailTemp.length >= 10 || self.root.project.activeProject?.thumbnailImages?.length === 0) {
        //   self.flushThumbnailTemp();
        // }
        // self.setDebouncedCallback(() => self.flushThumbnailTemp(), 500);

        self.onImageFastThumbnailAddedOrUpdated(event?.imageData);
        // console.log('#event - DataModel(ImageFastThumbnailAdded) - getSnapshot: ', getSnapshot(self));
      });

      // ON THUMBNAIL PROGRESS UPDATED
      self.addListener('ThumbnailProgressUpdated', (event: ThumbnailProgressUpdated) => {
        console.log('#event - DataModel(ThumbnailProgressUpdated) - event: ', event);
        self.setPercentage(Math.trunc(event.loadingPercentage));
        if (!event.success) {
          self.handleUploadError(event.filename, event?.errorCode);
        }
        if (self.fileErroCodes.length && Math.trunc(event.loadingPercentage) === 100) {
          self.setPercentage(0);
          self.root.project.setUploading(false);
          self.sendErrorAnalytic();
        }
        // console.log('#event - DataModel(ThumbnailProgressUpdated) - getSnapshot: ', getSnapshot(self));
      });

      // ON THUMBNAIL UPDATED
      self.addListener('ImageFastThumbnailUpdated', (event: ImageFastThumbnailUpdated) => {
        console.log('#event - DataModel(ImageFastThumbnailUpdated) - event: ', event);
        self.clearDebouncedCallback();
        self.addThumbnailTemp(event);
        if (self.thumbnailTemp.length >= 10) {
          self.flushThumbnailTemp();
        }
        self.setDebouncedCallback(() => self.flushThumbnailTemp(), 500);
        // console.log('#event - DataModel(ImageFastThumbnailUpdated) - getSnapshot: ', getSnapshot(self));
      });
      // ON THUMBNAIL DELETED
      self.addListener('ImageFastThumbnailsDeleted', (event: ImageFastThumbnailsDeleted) => {
        console.log('#event - DataModel(ImageFastThumbnailsDeleted) - event: ', event);
        self.onImageFastThumbnailDeleted(event);
        // console.log('#event - DataModel(UserImageDeleted) - getSnapshot: ', getSnapshot(self));
      });
      // ON USER IMAGE ADDED
      self.addListener('UserImageAdded', (event: UserImageAdded) => {
        console.log('#event - DataModel(UserImageAdded) - event: ', event);
        self.endPhotoPickerToFirstImageMeasure();
        self.onUserImageAddedOrUpdated(event);
        self.onUserTextAddedOrUpdated(event);
        console.log('#event - DataModel(UserImageAdded) - getSnapshot: ', getSnapshot(self));
      });
      // ON USER IMAGE UPDATED
      self.addListener('UserImageUpdated', (event: UserImageUpdated) => {
        console.log('#event - DataModel(UserImageUpdated) - event: ', event);
        self.onUserImageAddedOrUpdated(event);
        self.onUserTextAddedOrUpdated(event);
        // console.log('#event - DataModel(UserImageUpdated) - getSnapshot: ', getSnapshot(self));
      });
      // ON USER IMAGE DELETED
      self.addListener('UserImageDeleted', (event: UserImageDeleted) => {
        console.log('#event - DataModel(UserImageDeleted) - event: ', event);
        self.onUserImageDeleted(event);
        // console.log('#event - DataModel(UserImageDeleted) - getSnapshot: ', getSnapshot(self));
      });
      // ON IMAGE SHARE START
      self.addListener('ImageShareStart', (event: ImageShareStart) => {
        console.log('#event - DataModel(ImageShareStart) - event: ', event);
        self.setIsImageShareStarted(true);
        self.onImageShareStart(event);
      });
      // ON IMAGE SHARE UPLOAD COUNT
      self.addListener('ImageShareUploadCount', (event: ImageShareUploadCount) => {
        console.log('#event - DataModel(ImageShareUploadCount) - event: ', event);
        self.onImageShareUploadCount(event);
      });
      // ON IMAGE SHARE COMPLETE
      self.addListener('ImageShareComplete', (event: ImageShareComplete) => {
        console.log('#event - DataModel(ImageShareComplete) - event: ', event);
        self.setIsImageShareStarted(false);
        self.onImageShareComplete(event);
      });
      // ON IMAGE SHARE FAILURE
      self.addListener('ImageShareFailure', (event: ImageShareFailure) => {
        console.log('#event - DataModel(ImageShareFailure) - event: ', event);
        self.onImageShareFailure(event);
      });
      // ON IMAGE SHARE NEW NOTIFICATION
      self.addListener('ImageShareNewImageNotification', (event: ImageShareNewImageNotification) => {
        console.log('#event - DataModel(ImageShareNewImageNotification) - event: ', event);
        self.onImageShareNewImageNotification(event);
      });
      // ON IMAGE SHARE FAILURE
      self.addListener('ImageShareDownloadError', (event: ImageShareDownloadError) => {
        console.log('#event - DataModel(ImageShareDownloadError) - event: ', event);
        self.onImageShareDownloadError(event);
      });
      // ON ERROR
      self.addListener('Unauthorized', (event: unknown) => {
        console.error('#event - DataModel(Unauthorized) - event: ', event);
        Datadog.logInfo(DATADOG_CUSTOM_ACTIONS.user_unauthorized_event, { event });
        Datadog.logError(DATADOG_CUSTOM_ACTIONS.user_unauthorized_event, { event });
      });
      self.addListener('UserImageReplaced', (event: UserImageReplaced) => {
        console.log('#event - DataModel(UserImageReplaced) - event: ', event);
        self.onUserImageReplaced(event);
      });
    },
  }));
